/**
 * Certificates Page
 * 
 * Manage SSL/TLS certificates using CertificateInfo from @sbclite/core/schemas.
 * Refactored to use common components for consistent UI patterns.
 */

import React, { useRef, useState } from 'react';
import { 
  Shield, 
  Upload, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  Eye, 
  EyeOff, 
  Copy, 
  FileText, 
  Globe, 
  Key,
  Calendar,
  Server,
  Users,
  Building,
  Info,
  RefreshCw,
  X,
  AlertCircle,
  Pencil,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import type { PublicCertificateInfo as CertificateInfo } from '@sbclite/core/schemas';
import type { Security } from '@sbclite/core';
import toast from 'react-hot-toast';
import { useObservationTime } from '@/hooks/useObservationTime';
import { certificateDateStatus } from '@/lib/certificate-status';

// Common components
import {
  PageHeader,
  PageContainer,
  StatsGrid,
  StatCard,
  FilterBar,
  ItemCard,
  ItemCardIcon,
  ItemActions,
  EmptyState,
  LoadingState,
  ErrorState,
  DeleteConfirmModal,
  TabbedDetailsModal,
  TabContent,
  DetailSection,
  DetailGrid,
  DetailItem,
  QuickStatsGrid,
  QuickStatCard,
  type Tab,
  type FilterOption,
} from '../../components/common';
import { useViewMode } from '../../hooks/useViewMode';

// Use the CertificateInfo type from core schemas

// ========================================
// HELPER FUNCTIONS
// ========================================

function getDaysRemaining(validTo: string): number {
  const expiry = new Date(validTo);
  const now = new Date();
  return Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

function getDomainsFromSAN(subjectAltNames: string[] | null): string[] {
  if (!subjectAltNames || !Array.isArray(subjectAltNames)) return [];
  return subjectAltNames
    .filter(san => typeof san === 'string')
    .map(san => san.replace(/^DNS:/i, '').replace(/^IP:/i, ''));
}

function getCNFromDN(dn: string): string {
  if (!dn) return 'Unknown';
  const match = dn.match(/CN=([^,]+)/i);
  return match ? match[1] : dn;
}

function getOrgFromDN(dn: string): string | undefined {
  if (!dn) return undefined;
  const match = dn.match(/O=([^,]+)/i);
  return match ? match[1] : undefined;
}

function getCountryFromDN(dn: string): string | undefined {
  if (!dn) return undefined;
  const match = dn.match(/C=([^,]+)/i);
  return match ? match[1] : undefined;
}

function getApiErrorMessage(error: unknown, fallback: string) {
  if (typeof error === 'object' && error !== null) {
    const response = (error as { response?: { data?: { error?: string; message?: string } } }).response;
    const apiError = response?.data?.error || response?.data?.message;
    if (apiError) return apiError;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
}

// ========================================
// CONSTANTS
// ========================================

type FilterType = 'all' | 'valid' | 'expiring' | 'expired' | 'pending' | 'unknown' | 'server' | 'client' | 'ca' | 'intermediate';

const FILTER_OPTIONS: FilterOption[] = [
  { value: 'all', label: 'All Types' },
  { value: 'valid', label: 'Valid' },
  { value: 'expiring', label: 'Expiring Soon' },
  { value: 'expired', label: 'Expired' },
  { value: 'pending', label: 'Not yet valid' },
  { value: 'unknown', label: 'Unknown dates' },
  { value: 'server', label: 'Server' },
  { value: 'client', label: 'Client' },
  { value: 'ca', label: 'CA' },
  { value: 'intermediate', label: 'Intermediate' },
];

const DETAILS_TABS: Tab[] = [
  { id: 'overview', label: 'Overview', icon: Info },
  { id: 'details', label: 'Certificate Details', icon: FileText },
];

const TYPE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  server: Server,
  client: Users,
  ca: Building,
  intermediate: Key,
};

// ========================================
// MAIN COMPONENT
// ========================================

function CertificateUsageSummary({ certificateId }: { certificateId: string }) {
  const query = useQuery({ queryKey: ['certificate-usage', certificateId], queryFn: async () => (await api.get<{ data: Security.CertificateUsage }>(`/api/certificates/${encodeURIComponent(certificateId)}/usage`)).data.data });
  if (query.isLoading) return <p>Loading dependencies…</p>;
  if (query.error) return <ErrorState message={query.error.message} onRetry={() => void query.refetch()} />;
  return <div className="text-sm space-y-1">{query.data && Object.entries(query.data).map(([group, rows]) => <p key={group}>{group}: {rows.map(row => row.name).join(', ') || 'None'}</p>)}</div>;
}

const CertificatesPage: React.FC = () => {
  const now = useObservationTime();
  const queryClient = useQueryClient();
  const refreshDependencies = () => Promise.all(['certificates', 'certificates-dropdown', 'tls-contexts', 'hosts', 'sip-interfaces', 'sip-peers', 'container-prerequisites', 'certificate-usage'].map(key => queryClient.invalidateQueries({ queryKey: [key] })));
  
  // State management - use user preference for default view mode
  const [viewMode, setViewMode] = useViewMode();
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCertificate, setSelectedCertificate] = useState<CertificateInfo | null>(null);
  const [certificateToEdit, setCertificateToEdit] = useState<CertificateInfo | null>(null);
  const [certificateToDelete, setCertificateToDelete] = useState<CertificateInfo | null>(null);

  // API queries
  const certificatesQuery = useQuery({
    queryKey: ['certificates'],
    queryFn: async () => {
      return (await api.getCertificates()).data;
    }
  });
  
  const certificates = certificatesQuery.data || [];

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (certificateId: string) => {
      await api.delete(`/api/certificates/${encodeURIComponent(certificateId)}`);
    },
    onSuccess: () => {
      void refreshDependencies();
      setShowDeleteModal(false);
      setCertificateToDelete(null);
    },
    onError: (error: Error) => {
      console.error('Failed to delete certificate:', error);
      toast.error(getApiErrorMessage(error, 'Failed to delete certificate'));
    }
  });

  // Utility functions
  const getTypeIcon = (type: CertificateInfo['certificateType']) => {
    const Icon = TYPE_ICONS[type] || Shield;
    return <Icon className="w-4 h-4" />;
  };

  // Filter and search logic
  const filteredCertificates = certificates.filter(cert => {
    let matchesType = false;
    if (filterType === 'all') {
      matchesType = true;
    } else if (filterType === 'valid') {
      const validFrom = new Date(cert.validFrom).getTime();
      const validTo = new Date(cert.validTo).getTime();
      const now = Date.now();
      matchesType = validFrom <= now && validTo > now;
    } else if (filterType === 'expiring') {
      matchesType = certificateDateStatus(cert, now) === 'Expiring soon';
    } else if (filterType === 'expired') {
      matchesType = certificateDateStatus(cert, now) === 'Expired';
    } else if (filterType === 'pending') {
      matchesType = certificateDateStatus(cert, now) === 'Not yet valid';
    } else if (filterType === 'unknown') {
      matchesType = certificateDateStatus(cert, now) === 'Unknown dates';
    } else {
      matchesType = cert.certificateType === filterType;
    }

    const domains = getDomainsFromSAN(cert.subjectAltNames);
    const matchesSearch = searchQuery === '' || 
      cert.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      domains.some(domain => domain.toLowerCase().includes(searchQuery.toLowerCase())) ||
      getCNFromDN(cert.subjectDn).toLowerCase().includes(searchQuery.toLowerCase());

    return matchesType && matchesSearch;
  });

  const sortedCertificates = [...filteredCertificates].sort((a, b) => {
    return new Date(a.validTo).getTime() - new Date(b.validTo).getTime();
  });

  // Calculate statistics
  const stats = {
    total: certificates.length,
    active: certificates.filter(cert => {
      const validFrom = new Date(cert.validFrom).getTime();
      const validTo = new Date(cert.validTo).getTime();
      const now = Date.now();
      return validFrom <= now && validTo > now;
    }).length,
    expiringSoon: certificates.filter(cert => certificateDateStatus(cert, now) === 'Expiring soon').length,
    expired: certificates.filter(cert => certificateDateStatus(cert, now) === 'Expired').length
  };

  // Loading state
  if (certificatesQuery.isLoading) {
    return <LoadingState />;
  }

  // Error state
  if (certificatesQuery.error) {
    return (
      <ErrorState
        title="Failed to load certificates"
        message={certificatesQuery.error instanceof Error ? certificatesQuery.error.message : 'Unknown error'}
        onRetry={() => certificatesQuery.refetch()}
      />
    );
  }

  return (
    <PageContainer>
      {/* Header */}
      <PageHeader
        title="SSL/TLS Certificates"
        description="Manage your SSL/TLS certificates"
        onRefresh={() => void refreshDependencies()}
        isRefreshing={certificatesQuery.isFetching}
        primaryAction={{
          label: 'Upload Certificate',
          icon: Upload,
          onClick: () => setShowUploadModal(true),
        }}
      />

      <p className="text-sm text-gray-600">Date validity does not confirm trust, hostname coverage or engine application. TLS listeners select certificates through their SIP interface’s TLS context.</p>

      {/* Statistics Cards */}
      <StatsGrid>
        <StatCard
          label="Total Certificates"
          value={stats.total}
          icon={Shield}
          iconBgColor="bg-blue-100"
          iconColor="text-blue-600"
        />
        <StatCard
          label="Valid"
          value={stats.active}
          icon={CheckCircle}
          iconBgColor="bg-green-100"
          iconColor="text-green-600"
          valueColor="text-green-600"
        />
        <StatCard
          label="Expiring Soon"
          value={stats.expiringSoon}
          icon={Clock}
          iconBgColor="bg-yellow-100"
          iconColor="text-yellow-600"
          valueColor="text-yellow-600"
        />
        <StatCard
          label="Expired"
          value={stats.expired}
          icon={AlertCircle}
          iconBgColor="bg-red-100"
          iconColor="text-red-600"
          valueColor="text-red-600"
        />
      </StatsGrid>

      {/* Filters and Search */}
      <FilterBar
        searchPlaceholder="Search certificates..."
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        filterOptions={FILTER_OPTIONS}
        filterValue={filterType}
        onFilterChange={(value) => setFilterType(value as FilterType)}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />

      {/* Certificates Display */}
      {sortedCertificates.length === 0 ? (
        <EmptyState
          icon={Shield}
          title="No certificates found"
          description="Get started by uploading a certificate."
          isFiltered={searchQuery !== '' || filterType !== 'all'}
          action={{
            label: 'Upload Certificate',
            icon: Upload,
            onClick: () => setShowUploadModal(true),
          }}
        />
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {sortedCertificates.map((certificate) => {
            const daysRemaining = getDaysRemaining(certificate.validTo);
            const domains = getDomainsFromSAN(certificate.subjectAltNames);
            const isExpiringSoon = certificateDateStatus(certificate, now) === 'Expiring soon';
            const isExpired = certificateDateStatus(certificate, now) === 'Expired';

            return (
              <ItemCard
                key={certificate.certificateId}
                icon={
                  <ItemCardIcon 
                    icon={getTypeIcon(certificate.certificateType)} 
                    bgColor="bg-blue-100" 
                  />
                }
                title={certificate.name}
                subtitle={
                  domains.length > 0 
                    ? domains.slice(0, 2).join(', ') + (domains.length > 2 ? ` +${domains.length - 2} more` : '')
                    : getCNFromDN(certificate.subjectDn)
                }
                details={[
                  { label: 'Date status', value: certificateDateStatus(certificate, now) },
                  { label: 'Expires', value: new Date(certificate.validTo).toLocaleDateString() },
                  { 
                    label: 'Days remaining', 
                    value: (
                      <span className={isExpired ? 'text-red-600 font-medium' : isExpiringSoon ? 'text-yellow-600 font-medium' : 'text-gray-600'}>
                        {Number.isFinite(daysRemaining) ? daysRemaining : 'Unknown'}
                      </span>
                    )
                  },
                  { label: 'Key', value: `${certificate.publicKeyAlgorithm} ${certificate.keySize > 0 ? `${certificate.keySize}-bit` : '(size unknown)'}` },
                ]}
                actions={
                  <ItemActions
                    variant="card"
                    actions={[
                      {
                        kind: 'view',
                        onClick: () => {
                          setSelectedCertificate(certificate);
                          setShowDetailsModal(true);
                        },
                      },
                      {
                        kind: 'edit',
                        onClick: () => {
                          setCertificateToEdit(certificate);
                          setShowEditModal(true);
                        },
                      },
                      {
                        kind: 'delete',
                        onClick: () => {
                          setCertificateToDelete(certificate);
                          setShowDeleteModal(true);
                        },
                      },
                    ]}
                  />
                }
              />
            );
          })}
        </div>
      ) : (
        // List View (Table)
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Certificate</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expires</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Key</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedCertificates.map((certificate) => {
                const daysRemaining = getDaysRemaining(certificate.validTo);
                const domains = getDomainsFromSAN(certificate.subjectAltNames);
                const isExpiringSoon = certificateDateStatus(certificate, now) === 'Expiring soon';
                const isExpired = certificateDateStatus(certificate, now) === 'Expired';

                return (
                  <tr key={certificate.certificateId} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col">
                        <div className="text-sm font-medium text-gray-900">{certificate.name}</div>
                        <div className="text-xs text-gray-500">
                          {domains.length > 0 
                            ? domains.slice(0, 2).join(', ') + (domains.length > 2 ? ` +${domains.length - 2}` : '')
                            : getCNFromDN(certificate.subjectDn)
                          }
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                        {certificate.certificateType}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col text-sm">
                        <div className="text-gray-900">{new Date(certificate.validTo).toLocaleDateString()}</div>
                        <div className="text-xs text-gray-600">{certificateDateStatus(certificate, now)}</div>
                        <div className={`text-xs ${isExpired ? 'text-red-600 font-medium' : isExpiringSoon ? 'text-yellow-600 font-medium' : 'text-gray-600'}`}>
                          {daysRemaining} days
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {certificate.publicKeyAlgorithm} {certificate.keySize > 0 ? `${certificate.keySize}-bit` : '(size unknown)'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <ItemActions
                        variant="table"
                        actions={[
                          {
                            kind: 'view',
                            onClick: () => {
                              setSelectedCertificate(certificate);
                              setShowDetailsModal(true);
                            },
                          },
                          {
                            kind: 'edit',
                            onClick: () => {
                              setCertificateToEdit(certificate);
                              setShowEditModal(true);
                            },
                          },
                          {
                            kind: 'delete',
                            onClick: () => {
                              setCertificateToDelete(certificate);
                              setShowDeleteModal(true);
                            },
                          },
                        ]}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Upload Certificate Modal */}
      {showUploadModal && (
        <UploadCertificateModal
          onClose={() => setShowUploadModal(false)}
          onSuccess={() => {
            void refreshDependencies();
            setShowUploadModal(false);
          }}
        />
      )}

      {/* Certificate Details Modal */}
      {showDetailsModal && selectedCertificate && (
        <CertificateDetailsModal
          certificate={selectedCertificate}
          onEdit={() => {
            setShowDetailsModal(false);
            setSelectedCertificate(null);
            setCertificateToEdit(selectedCertificate);
            setShowEditModal(true);
          }}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedCertificate(null);
          }}
        />
      )}

      {/* Edit Certificate Modal */}
      {showEditModal && certificateToEdit && (
        <EditCertificateModal
          certificate={certificateToEdit}
          onClose={() => {
            setShowEditModal(false);
            setCertificateToEdit(null);
          }}
          onSuccess={() => {
            void refreshDependencies();
            setShowEditModal(false);
            setCertificateToEdit(null);
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {certificateToDelete && (
        <DeleteConfirmModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setCertificateToDelete(null);
          }}
          onConfirm={() => deleteMutation.mutate(certificateToDelete.certificateId)}
          title="Delete Certificate"
          entityType="certificate"
          entityName={certificateToDelete.name}
          additionalInfo={<CertificateUsageSummary certificateId={certificateToDelete.certificateId} />}
          warningMessage="Certificates in use must be unlinked before deletion."
          isDeleting={deleteMutation.isPending}
        />
      )}
    </PageContainer>
  );
};

// ========================================
// UPLOAD CERTIFICATE MODAL
// ========================================

interface UploadCertificateModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

const UploadCertificateModal: React.FC<UploadCertificateModalProps> = ({
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    certificateType: 'server' as CertificateInfo['certificateType'],
    certificatePem: '',
    privateKeyPem: '',
    caCertificatePem: ''
  });
  const [certificateInputMode, setCertificateInputMode] = useState<'paste' | 'upload'>('upload');
  const [privateKeyInputMode, setPrivateKeyInputMode] = useState<'paste' | 'upload'>('upload');
  const [caInputMode, setCaInputMode] = useState<'paste' | 'upload'>('upload');
  const certificateFileInputRef = useRef<HTMLInputElement | null>(null);
  const privateKeyFileInputRef = useRef<HTMLInputElement | null>(null);
  const caFileInputRef = useRef<HTMLInputElement | null>(null);
  const [showPrivateKey, setShowPrivateKey] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requiresPrivateKey = formData.certificateType === 'server' || formData.certificateType === 'client';

  const triggerFileSelect = (ref: React.RefObject<HTMLInputElement>, setMode: (mode: 'paste' | 'upload') => void) => {
    setMode('upload');
    window.requestAnimationFrame(() => ref.current?.click());
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const certificateData = {
        name: formData.name,
        description: formData.description || null,
        certificateType: formData.certificateType,
        certificateData: formData.certificatePem,
        privateKeyData: requiresPrivateKey
          ? formData.privateKeyPem
          : (formData.privateKeyPem.trim() ? formData.privateKeyPem : undefined),
        caCertificateData: formData.caCertificatePem || null,
      };

      await api.post('/api/certificates', certificateData);
      onSuccess();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to upload certificate'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>, field: 'certificatePem' | 'privateKeyPem' | 'caCertificatePem') => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const content = event.target?.result as string;
        setFormData(prev => ({ ...prev, [field]: content }));
      };
      reader.readAsText(file);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full h-[80vh] flex flex-col">
        <div className="p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Upload Certificate</h2>
              <p className="text-sm text-gray-600 mt-1">Upload an existing SSL/TLS certificate</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto flex-1">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Certificate Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="My SSL Certificate"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Optional description"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Certificate Type *
            </label>
            <select
              value={formData.certificateType}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                certificateType: e.target.value as CertificateInfo['certificateType']
              }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="server">Server</option>
              <option value="client">Client</option>
              <option value="ca">CA</option>
              <option value="intermediate">Intermediate</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {formData.certificateType === 'server' && 'Used for inbound TLS. Private key required.'}
              {formData.certificateType === 'client' && 'Used for outbound mTLS. Private key required.'}
              {formData.certificateType === 'ca' && 'Used to verify remote peers (no private key needed).'}
              {formData.certificateType === 'intermediate' && 'Chain certificate for verification (no private key needed).'}
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Certificate (PEM) *
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setCertificateInputMode('paste')}
                  className={`text-sm ${certificateInputMode === 'paste' ? 'text-blue-700' : 'text-gray-500'} hover:text-blue-700`}
                >
                  Paste
                </button>
                <button
                  type="button"
                  onClick={() => triggerFileSelect(certificateFileInputRef, setCertificateInputMode)}
                  className={`text-sm ${certificateInputMode === 'upload' ? 'text-blue-700' : 'text-gray-500'} hover:text-blue-700`}
                >
                  Upload
                </button>
              </div>
            </div>
            {certificateInputMode === 'upload' ? (
              <div>
                <input
                  ref={certificateFileInputRef}
                  type="file"
                  accept=".pem,.crt,.cer"
                  onChange={(e) => handleFileUpload(e, 'certificatePem')}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => triggerFileSelect(certificateFileInputRef, setCertificateInputMode)}
                  className="inline-flex items-center text-sm text-blue-600 hover:text-blue-700"
                >
                  Select certificate file
                </button>
              </div>
            ) : (
              <textarea
                value={formData.certificatePem}
                onChange={(e) => setFormData(prev => ({ ...prev, certificatePem: e.target.value }))}
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
                required
              />
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Private Key (PEM){requiresPrivateKey ? ' *' : ' (optional)'}
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPrivateKeyInputMode('paste')}
                  className={`text-sm ${privateKeyInputMode === 'paste' ? 'text-blue-700' : 'text-gray-500'} hover:text-blue-700`}
                >
                  Paste
                </button>
                <button
                  type="button"
                  onClick={() => triggerFileSelect(privateKeyFileInputRef, setPrivateKeyInputMode)}
                  className={`text-sm ${privateKeyInputMode === 'upload' ? 'text-blue-700' : 'text-gray-500'} hover:text-blue-700`}
                >
                  Upload
                </button>
                {privateKeyInputMode === 'paste' && (
                  <button
                    type="button"
                    onClick={() => setShowPrivateKey(!showPrivateKey)}
                    className="text-sm text-gray-600 hover:text-gray-800"
                  >
                    {showPrivateKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                )}
              </div>
            </div>
            {privateKeyInputMode === 'upload' ? (
              <div>
                <input
                  ref={privateKeyFileInputRef}
                  type="file"
                  accept=".pem,.key"
                  onChange={(e) => handleFileUpload(e, 'privateKeyPem')}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => triggerFileSelect(privateKeyFileInputRef, setPrivateKeyInputMode)}
                  className="inline-flex items-center text-sm text-blue-600 hover:text-blue-700"
                >
                  Select private key file
                </button>
              </div>
            ) : (
              <textarea
                value={formData.privateKeyPem}
                onChange={(e) => setFormData(prev => ({ ...prev, privateKeyPem: e.target.value }))}
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                placeholder="-----BEGIN PRIVATE KEY-----&#10;...&#10;-----END PRIVATE KEY-----"
                style={{ WebkitTextSecurity: showPrivateKey ? 'none' : 'disc' } as React.CSSProperties}
                required={requiresPrivateKey}
              />
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                CA Certificate Chain (PEM) - Optional
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setCaInputMode('paste')}
                  className={`text-sm ${caInputMode === 'paste' ? 'text-blue-700' : 'text-gray-500'} hover:text-blue-700`}
                >
                  Paste
                </button>
                <button
                  type="button"
                  onClick={() => triggerFileSelect(caFileInputRef, setCaInputMode)}
                  className={`text-sm ${caInputMode === 'upload' ? 'text-blue-700' : 'text-gray-500'} hover:text-blue-700`}
                >
                  Upload
                </button>
              </div>
            </div>
            {caInputMode === 'upload' ? (
              <div>
                <input
                  ref={caFileInputRef}
                  type="file"
                  accept=".pem,.crt,.cer"
                  onChange={(e) => handleFileUpload(e, 'caCertificatePem')}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => triggerFileSelect(caFileInputRef, setCaInputMode)}
                  className="inline-flex items-center text-sm text-blue-600 hover:text-blue-700"
                >
                  Select CA chain file
                </button>
              </div>
            ) : (
              <textarea
                value={formData.caCertificatePem}
                onChange={(e) => setFormData(prev => ({ ...prev, caCertificatePem: e.target.value }))}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
              />
            )}
            <p className="text-xs text-gray-500 mt-1">Include intermediate certificates if available</p>
          </div>

          <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={
                isLoading ||
                !formData.name ||
                !formData.certificatePem ||
                (requiresPrivateKey && !formData.privateKeyPem)
              }
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isLoading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Certificate
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ========================================
// EDIT CERTIFICATE MODAL
// ========================================

interface EditCertificateModalProps {
  certificate: CertificateInfo;
  onClose: () => void;
  onSuccess: () => void;
}

const EditCertificateModal: React.FC<EditCertificateModalProps> = ({
  certificate,
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState({
    name: certificate.name,
    description: certificate.description || '',
    certificateType: certificate.certificateType,
    certificatePem: certificate.certificateData,
    privateKeyPem: '',
    caCertificatePem: certificate.caCertificateData || ''
  });
  const [certificateInputMode, setCertificateInputMode] = useState<'paste' | 'upload'>('upload');
  const [privateKeyInputMode, setPrivateKeyInputMode] = useState<'paste' | 'upload'>('upload');
  const [caInputMode, setCaInputMode] = useState<'paste' | 'upload'>('upload');
  const certificateFileInputRef = useRef<HTMLInputElement | null>(null);
  const privateKeyFileInputRef = useRef<HTMLInputElement | null>(null);
  const caFileInputRef = useRef<HTMLInputElement | null>(null);
  const [showPrivateKey, setShowPrivateKey] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requiresPrivateKey = formData.certificateType === 'server' || formData.certificateType === 'client';

  const triggerFileSelect = (ref: React.RefObject<HTMLInputElement>, setMode: (mode: 'paste' | 'upload') => void) => {
    setMode('upload');
    window.requestAnimationFrame(() => ref.current?.click());
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const updatePayload = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        certificateType: formData.certificateType,
        certificateData: formData.certificatePem,
        privateKeyData: formData.privateKeyPem.trim() || undefined,
        caCertificateData: formData.caCertificatePem.trim() || null
      };

      await api.put(`/api/certificates/${encodeURIComponent(certificate.certificateId)}`, updatePayload);
      onSuccess();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to update certificate'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>, field: 'certificatePem' | 'privateKeyPem' | 'caCertificatePem') => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const content = event.target?.result as string;
        setFormData(prev => ({ ...prev, [field]: content }));
      };
      reader.readAsText(file);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full h-[80vh] flex flex-col">
        <div className="p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Edit Certificate</h2>
              <p className="text-sm text-gray-600 mt-1">Update certificate name and PEM data</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto flex-1">
          <p className="text-sm text-gray-600">Leave replacement private key blank to keep the stored key. A new certificate must match that key or a supplied replacement. Clearing the CA chain removes it. Saving does not confirm engine application.</p>
          <CertificateUsageSummary certificateId={certificate.certificateId} />
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Certificate Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="My SSL Certificate"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Optional description"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Certificate Type *
            </label>
            <select
              value={formData.certificateType}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                certificateType: e.target.value as CertificateInfo['certificateType']
              }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="server">Server</option>
              <option value="client">Client</option>
              <option value="ca">CA</option>
              <option value="intermediate">Intermediate</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {formData.certificateType === 'server' && 'Used for inbound TLS. Private key required.'}
              {formData.certificateType === 'client' && 'Used for outbound mTLS. Private key required.'}
              {formData.certificateType === 'ca' && 'Used to verify remote peers (no private key needed).'}
              {formData.certificateType === 'intermediate' && 'Chain certificate for verification (no private key needed).'}
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Certificate (PEM) *
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setCertificateInputMode('paste')}
                  className={`text-sm ${certificateInputMode === 'paste' ? 'text-blue-700' : 'text-gray-500'} hover:text-blue-700`}
                >
                  Paste
                </button>
                <button
                  type="button"
                  onClick={() => triggerFileSelect(certificateFileInputRef, setCertificateInputMode)}
                  className={`text-sm ${certificateInputMode === 'upload' ? 'text-blue-700' : 'text-gray-500'} hover:text-blue-700`}
                >
                  Upload
                </button>
              </div>
            </div>
            {certificateInputMode === 'upload' ? (
              <div>
                <input
                  ref={certificateFileInputRef}
                  type="file"
                  accept=".pem,.crt,.cer"
                  onChange={(e) => handleFileUpload(e, 'certificatePem')}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => triggerFileSelect(certificateFileInputRef, setCertificateInputMode)}
                  className="inline-flex items-center text-sm text-blue-600 hover:text-blue-700"
                >
                  Select certificate file
                </button>
              </div>
            ) : (
              <textarea
                value={formData.certificatePem}
                onChange={(e) => setFormData(prev => ({ ...prev, certificatePem: e.target.value }))}
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
                required
              />
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Private Key (PEM){requiresPrivateKey ? ' *' : ' (optional)'}
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPrivateKeyInputMode('paste')}
                  className={`text-sm ${privateKeyInputMode === 'paste' ? 'text-blue-700' : 'text-gray-500'} hover:text-blue-700`}
                >
                  Paste
                </button>
                <button
                  type="button"
                  onClick={() => triggerFileSelect(privateKeyFileInputRef, setPrivateKeyInputMode)}
                  className={`text-sm ${privateKeyInputMode === 'upload' ? 'text-blue-700' : 'text-gray-500'} hover:text-blue-700`}
                >
                  Upload
                </button>
                {privateKeyInputMode === 'paste' && (
                  <button
                    type="button"
                    onClick={() => setShowPrivateKey(!showPrivateKey)}
                    className="text-sm text-gray-600 hover:text-gray-800"
                  >
                    {showPrivateKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                )}
              </div>
            </div>
            {privateKeyInputMode === 'upload' ? (
              <div>
                <input
                  ref={privateKeyFileInputRef}
                  type="file"
                  accept=".pem,.key"
                  onChange={(e) => handleFileUpload(e, 'privateKeyPem')}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => triggerFileSelect(privateKeyFileInputRef, setPrivateKeyInputMode)}
                  className="inline-flex items-center text-sm text-blue-600 hover:text-blue-700"
                >
                  Select private key file
                </button>
              </div>
            ) : (
              <textarea
                value={formData.privateKeyPem}
                onChange={(e) => setFormData(prev => ({ ...prev, privateKeyPem: e.target.value }))}
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                placeholder="-----BEGIN PRIVATE KEY-----&#10;...&#10;-----END PRIVATE KEY-----"
                style={{ WebkitTextSecurity: showPrivateKey ? 'none' : 'disc' } as React.CSSProperties}
                required={requiresPrivateKey && !certificate.hasPrivateKey}
              />
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                CA Certificate Chain (PEM) - Optional
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setCaInputMode('paste')}
                  className={`text-sm ${caInputMode === 'paste' ? 'text-blue-700' : 'text-gray-500'} hover:text-blue-700`}
                >
                  Paste
                </button>
                <button
                  type="button"
                  onClick={() => triggerFileSelect(caFileInputRef, setCaInputMode)}
                  className={`text-sm ${caInputMode === 'upload' ? 'text-blue-700' : 'text-gray-500'} hover:text-blue-700`}
                >
                  Upload
                </button>
              </div>
            </div>
            {caInputMode === 'upload' ? (
              <div>
                <input
                  ref={caFileInputRef}
                  type="file"
                  accept=".pem,.crt,.cer"
                  onChange={(e) => handleFileUpload(e, 'caCertificatePem')}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => triggerFileSelect(caFileInputRef, setCaInputMode)}
                  className="inline-flex items-center text-sm text-blue-600 hover:text-blue-700"
                >
                  Select CA chain file
                </button>
              </div>
            ) : (
              <textarea
                value={formData.caCertificatePem}
                onChange={(e) => setFormData(prev => ({ ...prev, caCertificatePem: e.target.value }))}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
              />
            )}
            <p className="text-xs text-gray-500 mt-1">Include intermediate certificates if available</p>
          </div>

          <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={
                isLoading ||
                !formData.name ||
                !formData.certificatePem ||
                (requiresPrivateKey && !certificate.hasPrivateKey && !formData.privateKeyPem)
              }
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isLoading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Pencil className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ========================================
// CERTIFICATE DETAILS MODAL
// ========================================

interface CertificateDetailsModalProps {
  certificate: CertificateInfo;
  onEdit: () => void;
  onClose: () => void;
}

const CertificateDetailsModal: React.FC<CertificateDetailsModalProps> = ({
  certificate,
  onEdit,
  onClose
}) => {
  const [activeTab, setActiveTab] = useState<string>('overview');
  const now = useObservationTime();

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const daysRemaining = getDaysRemaining(certificate.validTo);
  const isExpiringSoon = certificateDateStatus(certificate, now) === 'Expiring soon';
  const isExpired = certificateDateStatus(certificate, now) === 'Expired';
  const domains = getDomainsFromSAN(certificate.subjectAltNames);

  const headerBanner = <><p className="text-sm text-gray-600">{certificateDateStatus(certificate, now)}. Dates do not confirm trust, hostname coverage or engine application.</p>{(isExpiringSoon || isExpired) && (
    <div className={`mt-4 p-3 rounded-lg ${isExpired ? 'bg-red-50 border border-red-200' : 'bg-yellow-50 border border-yellow-200'}`}>
      <div className="flex items-center">
        <AlertTriangle className={`w-5 h-5 mr-3 ${isExpired ? 'text-red-500' : 'text-yellow-500'}`} />
        <div>
          <p className={`font-medium ${isExpired ? 'text-red-800' : 'text-yellow-800'}`}>
            {isExpired ? 'Certificate Expired' : 'Certificate Expiring Soon'}
          </p>
          <p className={`text-sm ${isExpired ? 'text-red-600' : 'text-yellow-600'}`}>
            {isExpired 
              ? `This certificate expired ${Math.abs(daysRemaining)} days ago`
              : `This certificate will expire in ${daysRemaining} days`
            }
          </p>
        </div>
      </div>
    </div>
  )}</>;

  return (
    <TabbedDetailsModal
      isOpen={true}
      onClose={onClose}
      title={certificate.name}
      subtitle="Certificate Details"
      tabs={DETAILS_TABS}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      headerBanner={headerBanner}
      footer={
        <div className="p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={onEdit}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Edit Certificate
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      }
    >
      {activeTab === 'overview' && (
        <TabContent>
          {/* Quick Stats */}
          <QuickStatsGrid>
            <QuickStatCard
              icon={<div className="p-2 bg-blue-100 rounded-lg"><Shield className="w-5 h-5 text-blue-600" /></div>}
              label="Certificate Type"
              value={<span className="capitalize">{certificate.certificateType}</span>}
            />
            <QuickStatCard
              icon={<div className="p-2 bg-green-100 rounded-lg"><Calendar className="w-5 h-5 text-green-600" /></div>}
              label="Days Remaining"
              value={daysRemaining}
              valueColor={isExpired ? 'text-red-600' : isExpiringSoon ? 'text-yellow-600' : 'text-green-600'}
            />
            <QuickStatCard
              icon={<div className="p-2 bg-purple-100 rounded-lg"><Key className="w-5 h-5 text-purple-600" /></div>}
              label="Key Algorithm"
              value={`${certificate.publicKeyAlgorithm} ${certificate.keySize > 0 ? certificate.keySize : '(size unknown)'}`}
            />
          </QuickStatsGrid>

          {/* Domains */}
          {domains.length > 0 && (
            <DetailSection title="Domains">
              <div className="flex flex-wrap gap-2">
                {domains.map((domain, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                  >
                    <Globe className="w-4 h-4 mr-1" />
                    {domain}
                  </span>
                ))}
              </div>
            </DetailSection>
          )}

          {/* Validity Period */}
          <DetailSection title="Validity Period">
            <DetailGrid>
              <DetailItem label="Valid From" value={new Date(certificate.validFrom).toLocaleString()} />
              <DetailItem label="Valid Until" value={new Date(certificate.validTo).toLocaleString()} />
            </DetailGrid>
          </DetailSection>

          {/* Auto-renewal */}
          {certificate.autoRenew && (
            <DetailSection title="Auto-renewal">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center">
                  <RefreshCw className="w-5 h-5 text-green-600 mr-3" />
                  <div>
                    <p className="font-medium text-green-800">Auto-renewal enabled</p>
                    <p className="text-sm text-green-600">
                      Will attempt renewal {certificate.renewalThreshold || 30} days before expiry
                    </p>
                  </div>
                </div>
              </div>
            </DetailSection>
          )}
        </TabContent>
      )}

      {activeTab === 'details' && (
        <TabContent>
          {/* Certificate Information */}
          <DetailSection title="Certificate Information">
            <div className="bg-gray-50 rounded-lg p-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-600">Serial Number</p>
                  <div className="flex items-center space-x-2">
                    <p className="text-gray-900 font-mono text-sm">{certificate.serialNumber}</p>
                    <button
                      onClick={() => copyToClipboard(certificate.serialNumber)}
                      className="p-1 text-gray-400 hover:text-gray-600"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Fingerprint (SHA-256)</p>
                  <div className="flex items-center space-x-2">
                    <p className="text-gray-900 font-mono text-sm break-all">{certificate.fingerprint}</p>
                    <button
                      onClick={() => copyToClipboard(certificate.fingerprint)}
                      className="p-1 text-gray-400 hover:text-gray-600"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </DetailSection>

          {/* Issuer and Subject */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <DetailSection title="Issuer">
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div>
                  <p className="text-sm font-medium text-gray-600">Common Name</p>
                  <p className="text-gray-900">{getCNFromDN(certificate.issuerDn)}</p>
                </div>
                {getOrgFromDN(certificate.issuerDn) && (
                  <div>
                    <p className="text-sm font-medium text-gray-600">Organization</p>
                    <p className="text-gray-900">{getOrgFromDN(certificate.issuerDn)}</p>
                  </div>
                )}
                {getCountryFromDN(certificate.issuerDn) && (
                  <div>
                    <p className="text-sm font-medium text-gray-600">Country</p>
                    <p className="text-gray-900">{getCountryFromDN(certificate.issuerDn)}</p>
                  </div>
                )}
              </div>
            </DetailSection>

            <DetailSection title="Subject">
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div>
                  <p className="text-sm font-medium text-gray-600">Common Name</p>
                  <p className="text-gray-900">{getCNFromDN(certificate.subjectDn)}</p>
                </div>
                {getOrgFromDN(certificate.subjectDn) && (
                  <div>
                    <p className="text-sm font-medium text-gray-600">Organization</p>
                    <p className="text-gray-900">{getOrgFromDN(certificate.subjectDn)}</p>
                  </div>
                )}
                {getCountryFromDN(certificate.subjectDn) && (
                  <div>
                    <p className="text-sm font-medium text-gray-600">Country</p>
                    <p className="text-gray-900">{getCountryFromDN(certificate.subjectDn)}</p>
                  </div>
                )}
              </div>
            </DetailSection>
          </div>

          {/* Certificate Content */}
          <DetailSection title="Certificate (PEM)">
            <div className="bg-gray-900 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-300">Certificate</span>
                <button
                  onClick={() => copyToClipboard(certificate.certificateData)}
                  className="text-gray-400 hover:text-white"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              <pre className="text-sm text-gray-300 overflow-x-auto whitespace-pre-wrap break-all max-h-48">
                {certificate.certificateData}
              </pre>
            </div>
          </DetailSection>

          {/* Private Key */}
          <DetailSection title="Private key"><p>{certificate.hasPrivateKey ? 'Stored on server; not returned to the browser. Supply a replacement key when rotating certificate material.' : 'No private key stored.'}</p></DetailSection>

          {/* CA Certificate */}
          {certificate.caCertificateData && (
            <DetailSection title="CA Certificate Chain">
              <div className="bg-gray-900 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-300">CA Certificate</span>
                  <button
                    onClick={() => copyToClipboard(certificate.caCertificateData!)}
                    className="text-gray-400 hover:text-white"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
                <pre className="text-sm text-gray-300 overflow-x-auto whitespace-pre-wrap break-all max-h-48">
                  {certificate.caCertificateData}
                </pre>
              </div>
            </DetailSection>
          )}
        </TabContent>
      )}
    </TabbedDetailsModal>
  );
};

export default CertificatesPage;
